import React, { useEffect, useMemo, useState } from "react";
import {
    Dialog,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/Modal";
import { ButtonShadcn as Button } from "@/components/ui/Button";
import RHFTextArea from "@/components/form/RHFTextArea";
import { useForm, FormProvider } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import toast from "react-hot-toast";
import { X } from "lucide-react";
import { RoleService } from "@/services/roleService";
import {
    deleteRoleReasonDefaultValues,
    deleteRoleReasonSchema,
} from "../validation.schema";
import { apiErrorMessageFormat } from "@/utils/formErrorUtils";

const DeleteRoleModal = ({ open, onModelClose, roleId, loading = false, setSilentFetch }) => {
    // Tracks local submit state
    const [submitting, setSubmitting] = useState(false);

    // React Hook Form setup with Zod validation
    const methods = useForm({
        resolver: zodResolver(deleteRoleReasonSchema),
        mode: "onChange",
        defaultValues: deleteRoleReasonDefaultValues,
    });

    const { handleSubmit, reset } = methods;

    // Reset form whenever modal opens
    useEffect(() => {
        if (!open) return;
        reset(deleteRoleReasonDefaultValues);
        setSubmitting(false);
    }, [open, reset]);

    // Combine external loading + internal submitting state
    const effectiveLoading = useMemo(() => loading || submitting, [loading, submitting]);

    // Check if delete action is allowed
    const canDelete = useMemo(() => {
        return !effectiveLoading && !!roleId;
    }, [effectiveLoading, roleId]);

    // Handle form submission
    const submitHandler = async (formData) => {
        if (!roleId) return;
        if (!canDelete) return;

        setSubmitting(true);

        try {
            // Call API to delete role with reason
            await RoleService.deleteRole(roleId, formData.reason.trim());

            toast.success("Role deleted");

            onModelClose?.();
            setSilentFetch(true);
        } catch (e) {
            console.error(e);

            toast.error(apiErrorMessageFormat(e, `Failed to delete role`));

            setSubmitting(false);
        }
    };

    return (
        <Dialog
            open={open}
            onOpenChange={(nextOpen) => {
                // Close modal when user dismisses dialog
                if (!nextOpen) onModelClose?.();
            }}
        >
            <DialogContent className="w-[90%] md:w-full flex flex-col p-0">
                <DialogHeader className="relative px-5 py-3 border-b border-border-primary shrink-0">
                    <div>
                        {/* Modal title */}
                        <DialogTitle className="text-[15px] font-semibold text-text-primary">
                            Delete Role
                        </DialogTitle>

                        {/* Warning message */}
                        <DialogDescription className="text-[12px] text-text-primary/70 mt-0.5">
                            This action cannot be undone. Provide a reason before deleting.
                        </DialogDescription>
                    </div>

                    {/* Close button */}
                    <DialogClose className="cursor-pointer absolute right-5 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-background-icon/40 flex items-center justify-center text-text-primary hover:bg-background-icon/60 transition-colors">
                        <span className="w-7 h-7 rounded-full bg-background-icon/40 flex items-center justify-center text-text-primary hover:bg-background-icon/60 transition-colors">
                            <X size={16} strokeWidth={3} />
                        </span>
                    </DialogClose>
                </DialogHeader>

                {/* Form wrapper */}
                <FormProvider {...methods}>
                    <form onSubmit={handleSubmit(submitHandler)}>
                        <div className="px-5 py-4">

                            {/* Reason input field */}
                            <RHFTextArea
                                name="reason"
                                label="Reason"
                                placeholder="Enter reason for deletion..."
                                required
                                rows={5}
                            />

                            {/* Action buttons */}
                            <div className="flex justify-end gap-3 mt-6">

                                {/* Cancel button closes modal */}
                                <DialogClose
                                    render={
                                        <Button
                                            variant="outline"
                                            className="bg-button-tertiary border-none"
                                            type="button"
                                            disabled={effectiveLoading}
                                        >
                                            Cancel
                                        </Button>
                                    }
                                />

                                {/* Submit delete action */}
                                <Button
                                    className="bg-destructive text-white"
                                    type="submit"
                                    disabled={!canDelete}
                                >
                                    {effectiveLoading ? "Deleting…" : "Delete Role"}
                                </Button>
                            </div>
                        </div>
                    </form>
                </FormProvider>
            </DialogContent>
        </Dialog>
    );
};

export default DeleteRoleModal;